import type { FramePacket, StreamInfo } from '../ws/types.ts';
import type { StreamDecoderMetrics } from './media-engine.worker.ts';

export interface MediaEngineCallbacks {
  onVideoPacket?: (packet: FramePacket) => void; // legado
  onAudioPacket?: (streamId: string, channels: number, data: Uint8Array, captureTimestamp: number, sampleCount: number, presentationTime?: number) => void;
  onAudioFrame?: (streamId: string, audioBuffer: ArrayBuffer, presentationTime: number, channels: number, sampleCount: number, sampleRate: number, timestamp: number) => void;
  onInfo?: (message: string) => void;
  onState?: (streamId: string, broadcasters: number, viewers: number) => void;
  onStreams?: (streams: StreamInfo[]) => void;
  onDisconnect?: () => void;
  onVideoFrame?: (streamId: string, frame: VideoFrame, presentationTime: number) => void;
  onFirstFrame?: (streamId: string) => void;
  onMetrics?: (streamId: string, metrics: StreamDecoderMetrics) => void;
  onRefresh?: (expected?: string) => void;
  onWorkerRefresh?: (expected?: string) => void;
}

export interface MediaEngineHandle {
  stop: () => void;
  send: (data: Record<string, unknown>) => void;
  setWatching: (streamId: string, watching: boolean) => void;
  setLatencyMode: (streamId: string, mode: 'low' | 'smooth') => void;
  requestSnapshot: (streamId: string) => void;
  registerVideoStream: (streamId: string) => void;
  unregisterVideoStream: (streamId: string) => void;
  setVideoPaused: (streamId: string, paused: boolean) => void;
}

export function startMediaEngine(
  wsUrl: string,
  callbacks: MediaEngineCallbacks,
  streamId: string = '*',
  liveId: string = '',
  liveToken: string = ''
): MediaEngineHandle {
  const worker = new Worker(new URL('./media-engine.worker.ts', import.meta.url), { type: 'module' });

  worker.postMessage({ type: 'init', diagLogs: false });
  worker.postMessage({ type: 'start', wsUrl, streamId, liveId, liveToken });

  let proxyFramesReceived = 0;
  let proxyFramesDispatched = 0;
  let proxyMaxDispatchMs = 0;
  let proxyLastFrameAt = 0;
  (window as any).__viewerPerf = (window as any).__viewerPerf || {};
  (window as any).__viewerPerf.getProxyStats = () => ({
    received: proxyFramesReceived,
    dispatched: proxyFramesDispatched,
    maxDispatchMs: proxyMaxDispatchMs,
    lastFrameAt: proxyLastFrameAt,
  });

  worker.onmessage = (event) => {
    const { type, ...payload } = event.data;
    switch (type) {
      case 'info':
        callbacks.onInfo?.(payload.message || '');
        break;
      case 'state':
        callbacks.onState?.(payload.streamId, payload.broadcasters || 0, payload.viewers || 0);
        break;
      case 'streams':
        callbacks.onStreams?.(payload.streams || []);
        break;
      case 'disconnect':
        callbacks.onDisconnect?.();
        break;
      case 'audioPacket':
        callbacks.onAudioPacket?.(
          payload.streamId,
          payload.channels,
          new Uint8Array(event.data.data),
          payload.captureTimestamp,
          payload.sampleCount,
          payload.presentationTime
        );
        break;
      case 'audioFrame':
        callbacks.onAudioFrame?.(
          payload.streamId,
          event.data.data as ArrayBuffer,
          payload.presentationTime,
          payload.channels,
          payload.sampleCount,
          payload.sampleRate,
          payload.timestamp
        );
        break;
      case 'videoFrame': {
        proxyFramesReceived++;
        const t0 = performance.now();
        callbacks.onVideoFrame?.(payload.streamId, event.data.frame as VideoFrame, payload.presentationTime);
        const dt = performance.now() - t0;
        proxyFramesDispatched++;
        proxyLastFrameAt = performance.now();
        if (dt > proxyMaxDispatchMs) proxyMaxDispatchMs = dt;
        // Log removido para nao poluir o console durante playback normal.
        // As estatísticas continuam disponíveis via (window as any).__viewerPerf.getProxyStats().
        break;
      }
      case 'firstFrame':
        callbacks.onFirstFrame?.(payload.streamId);
        break;
      case 'metrics':
        callbacks.onMetrics?.(payload.streamId, payload.metrics);
        break;
      case 'refresh':
        callbacks.onRefresh?.(payload.expected);
        break;
      case 'worker-refresh':
        callbacks.onWorkerRefresh?.(payload.expected);
        break;
    }
  };

  worker.onerror = (err) => {
    console.error('[media-engine proxy] worker error', err);
    callbacks.onInfo?.('erro no media engine');
  };

  return {
    stop: () => {
      worker.postMessage({ type: 'stop' });
      worker.terminate();
    },
    send: (data: Record<string, unknown>) => {
      worker.postMessage({ type: 'send', data });
    },
    setWatching: (streamId: string, watching: boolean) => {
      worker.postMessage({ type: 'setWatching', streamId, watching });
    },
    setLatencyMode: (streamId: string, mode: 'low' | 'smooth') => {
      worker.postMessage({ type: 'setLatencyMode', streamId, mode });
    },
    requestSnapshot: (streamId: string) => {
      worker.postMessage({ type: 'requestSnapshot', streamId });
    },
    registerVideoStream: (streamId: string) => {
      worker.postMessage({ type: 'registerVideoStream', streamId });
    },
    unregisterVideoStream: (streamId: string) => {
      worker.postMessage({ type: 'unregisterVideoStream', streamId });
    },
    setVideoPaused: (streamId: string, paused: boolean) => {
      worker.postMessage({ type: 'setVideoPaused', streamId, paused });
    },
  };
}
